import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { message } from '@/components'
import { Button } from '@/components/base/Button'
import { Modal } from '@/components/base/Modal'
import { MfaCodeInput } from '@/components/mfa/MfaCodeInput'
import { MfaRecoveryKeyInput } from '@/components/mfa/MfaRecoveryKeyInput'
import { useAuthStore } from '@/stores/auth'
import { getErrorCodeMessage } from '@/utils/error'

interface MfaChallengeModalProps {
  open: boolean
  mfaChallenge: string
  email: string
  scope?: number
  saveAccount?: boolean
  onSuccess: () => void
  onBack: () => void
  onExpired: () => void
}

type ChallengeMode = 'totp' | 'recovery'

const RECOVERY_KEY_LENGTH = 8

export function MfaChallengeModal({
  open,
  mfaChallenge,
  email,
  scope,
  saveAccount = true,
  onSuccess,
  onBack,
  onExpired,
}: MfaChallengeModalProps) {
  const { t } = useTranslation()
  const authStore = useAuthStore()
  const [mode, setMode] = useState<ChallengeMode>('totp')
  const [code, setCode] = useState('')
  const [recoveryKey, setRecoveryKey] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const resetState = () => {
    setMode('totp')
    setCode('')
    setRecoveryKey('')
    setError('')
  }

  const handleContinue = async (submittedCode?: string) => {
    const verificationCode = submittedCode ?? code
    if (verificationCode.length !== 6 || loading) {
      return
    }

    setLoading(true)
    setError('')

    try {
      const result = await authStore.completeMfaLogin(
        mfaChallenge,
        verificationCode,
        email,
        saveAccount,
        scope,
      )

      if (result && result.code !== 0) {
        if (result.code === 240) {
          onExpired()
          return
        }
        const errorMessage = getErrorCodeMessage(result.code, t)
        setCode('')
        setError(errorMessage)
        message.error(errorMessage)
        return
      }

      onSuccess()
    } finally {
      setLoading(false)
    }
  }

  const handleDisableViaRecovery = async (submittedKey?: string) => {
    const proof = submittedKey ?? recoveryKey
    if (proof.length !== RECOVERY_KEY_LENGTH || loading) {
      return
    }

    setLoading(true)
    setError('')

    try {
      const result = await authStore.completeMfaLoginWithRecovery(
        mfaChallenge,
        proof,
        email,
        saveAccount,
        scope,
      )

      if (result && result.code !== 0) {
        if (result.code === 240) {
          onExpired()
          return
        }
        const errorMessage = getErrorCodeMessage(result.code, t)
        setRecoveryKey('')
        setError(errorMessage)
        message.error(errorMessage)
        return
      }

      onSuccess()
    } finally {
      setLoading(false)
    }
  }

  // Modal close (X) or Back on the TOTP screen: exit the whole challenge.
  const handleClose = () => {
    resetState()
    onBack()
  }

  const switchToRecovery = () => {
    setError('')
    setCode('')
    setMode('recovery')
  }

  // Back on the recovery screen returns to the TOTP screen, not out of the flow.
  const backToTotp = () => {
    setError('')
    setRecoveryKey('')
    setMode('totp')
  }

  const isRecovery = mode === 'recovery'

  return (
    <Modal className="w-[480px]" open={open} onClose={handleClose}>
      <Modal.Header>
        {isRecovery ? t('mfa.challenge.recoveryTitle') : t('mfa.challenge.title')}
      </Modal.Header>
      <Modal.Body>
        {isRecovery ? (
          <>
            <p className="t-sm text-secondary mb-6">{t('mfa.challenge.recoveryDescription')}</p>
            <MfaRecoveryKeyInput
              value={recoveryKey}
              onChange={(value) => {
                setRecoveryKey(value)
                if (error) {
                  setError('')
                }
              }}
              onComplete={handleDisableViaRecovery}
              disabled={loading}
              hasError={!!error}
              autoFocus
            />
          </>
        ) : (
          <>
            <p className="t-sm text-secondary mb-6">{t('mfa.challenge.description')}</p>
            <MfaCodeInput
              value={code}
              onChange={(value) => {
                setCode(value)
                if (error) {
                  setError('')
                }
              }}
              onComplete={handleContinue}
              disabled={loading}
              hasError={!!error}
              autoFocus
            />
          </>
        )}
        {error && <p className="mt-3 t-sm text-error">{error}</p>}
      </Modal.Body>
      <Modal.Footer>
        {isRecovery ? (
          <div className="flex w-full items-center justify-end gap-3">
            <Button type="outline" onClick={backToTotp} disabled={loading} className="min-w-[88px]">
              {t('mfa.challenge.back')}
            </Button>
            <Button
              type="danger"
              onClick={() => handleDisableViaRecovery()}
              disabled={loading || recoveryKey.length !== RECOVERY_KEY_LENGTH}
              className="min-w-[120px]"
            >
              {t('mfa.challenge.disableMfa')}
            </Button>
          </div>
        ) : (
          <div className="flex w-full items-center justify-between gap-3">
            <button
              type="button"
              className="t-sm text-btn-secondary disabled:opacity-60"
              onClick={switchToRecovery}
              disabled={loading}
            >
              {t('mfa.challenge.disableViaRecovery')}
            </button>
            <div className="flex items-center gap-3">
              <Button
                type="outline"
                onClick={handleClose}
                disabled={loading}
                className="min-w-[88px]"
              >
                {t('mfa.challenge.back')}
              </Button>
              <Button
                type="secondary"
                onClick={() => handleContinue()}
                disabled={loading || code.length !== 6}
                className="min-w-[88px]"
              >
                {t('mfa.challenge.continue')}
              </Button>
            </div>
          </div>
        )}
      </Modal.Footer>
    </Modal>
  )
}
